import { useState, useEffect, useRef, useCallback } from "react";
import { drawTechnicalStitches } from "./technicalStitches";
import { stringify } from "svgson";
import { subsampleStitches } from "./stitchParser";

// ── Canvas constants ──────────────────────────────────────────────────────────
export const BASE_W = 200;
export const BASE_H = 218;
const RENDER_SCALE = 4;
export const VIEWPORT_W = 340;
export const VIEWPORT_H = 250;

export const centerPanForZoom = (z) => ({
  x: (VIEWPORT_W - BASE_W * z) / 2,
  y: (VIEWPORT_H - BASE_H * z) / 2,
});

// ── Color helpers ─────────────────────────────────────────────────────────────
function parseHex(hex) {
  const m = hex.match(/^#?([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i);
  if (!m) return [128, 128, 128];
  return [parseInt(m[1], 16), parseInt(m[2], 16), parseInt(m[3], 16)];
}

function rgbToHls(r, g, b) {
  r /= 255; g /= 255; b /= 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h, s;
  const l = (max + min) / 2;
  if (max === min) { h = 0; s = 0; }
  else {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
    else if (max === g) h = ((b - r) / d + 2) / 6;
    else h = ((r - g) / d + 4) / 6;
  }
  return [h, l, s];
}

function hlsToRgb(h, l, s) {
  if (s === 0) { const v = Math.round(l * 255); return [v, v, v]; }
  const hue2rgb = (p, q, t) => {
    if (t < 0) t += 1; if (t > 1) t -= 1;
    if (t < 1 / 6) return p + (q - p) * 6 * t;
    if (t < 1 / 2) return q;
    if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
    return p;
  };
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;
  return [
    Math.round(hue2rgb(p, q, h + 1 / 3) * 255),
    Math.round(hue2rgb(p, q, h) * 255),
    Math.round(hue2rgb(p, q, h - 1 / 3) * 255),
  ];
}

export function darkerHex(hex) {
  const [r, g, b] = parseHex(hex);
  let [h, l, s] = rgbToHls(r, g, b);
  // Shadowed rim/base of the thread. Darken strongly for mid & dark colours to
  // give depth, but only gently for light colours so white/cream thread doesn't
  // read as grey against a light fabric.
  let dl = l * 0.75;
  if (l > 0.6) {
    const t = (l - 0.6) / 0.4; // 0 at l=0.6 → 1 at l=1
    dl = dl + (l - dl) * t * 0.72; // pull back toward the true lightness
  }
  const [rr, gg, bb] = hlsToRgb(h, dl, s);
  return `rgb(${rr},${gg},${bb})`;
}

export function lightenHex(hex, amt) {
  const [r, g, b] = parseHex(hex);
  return `rgb(${Math.min(255, r + amt)},${Math.min(255, g + amt)},${Math.min(255, b + amt)})`;
}

export function threadBaseColor(hex) {
  const [r, g, b] = parseHex(hex);
  let [h, l, s] = rgbToHls(r, g, b);
  // Lit top of the thread. Keep light threads light (the old 0.85 cap turned
  // white thread grey); only hold pure white back a hair for definition.
  if (l > 0.97) l = 0.97;
  s = Math.min(1, s + 0.1);
  const [rr, gg, bb] = hlsToRgb(h, l, s);
  return `rgb(${rr},${gg},${bb})`;
}

// ── StitchPreviewCanvas ───────────────────────────────────────────────────────
const StitchPreviewCanvas = ({
  svgJoined,
  stitchSettings,
  targetWidth,
  targetHeight,
  realStitchData,
  zoom,
  setZoom,
  pan,
  setPan,
  panRef,
  viewportWidth,
  viewportHeight,
  fillViewport = false,
  technical = null,
}) => {
  const VW = viewportWidth ?? VIEWPORT_W;
  const VH = viewportHeight ?? VIEWPORT_H;
  const canvasWidth = fillViewport ? VW : BASE_W;
  const canvasHeight = fillViewport ? VH : BASE_H;
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const renderIdRef = useRef(0);
  const dragRef = useRef(null);
  const activePointersRef = useRef(new Map());
  const pinchRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const zoomRef = useRef(zoom);
  zoomRef.current = zoom;

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const onWheel = (e) => {
      e.preventDefault();
      const factor = e.deltaY < 0 ? 1.18 : 0.85;
      const rect = container.getBoundingClientRect();
      const cursorX = e.clientX - rect.left;
      const cursorY = e.clientY - rect.top;
      setZoom((prevZoom) => {
        const newZoom = Math.max(0.5, Math.min(10, prevZoom * factor));
        const scale = newZoom / prevZoom;
        const newPan = {
          x: cursorX - scale * (cursorX - panRef.current.x),
          y: cursorY - scale * (cursorY - panRef.current.y),
        };
        panRef.current = newPan;
        setPan(newPan);
        return newZoom;
      });
    };
    container.addEventListener("wheel", onWheel, { passive: false });
    return () => container.removeEventListener("wheel", onWheel);
  }, [setZoom, setPan, panRef]);

  const clampZoom = useCallback((value) => Math.max(0.5, Math.min(10, value)), []);

  const getPinchMetrics = useCallback((pointers) => {
    if (pointers.length < 2) return null;
    const [a, b] = pointers;
    const dx = b.clientX - a.clientX;
    const dy = b.clientY - a.clientY;
    const distance = Math.hypot(dx, dy) || 1;
    const rect = containerRef.current?.getBoundingClientRect();
    return {
      distance,
      centerX: (a.clientX + b.clientX) / 2 - (rect?.left || 0),
      centerY: (a.clientY + b.clientY) / 2 - (rect?.top || 0),
    };
  }, []);

  const onPointerDown = useCallback((e) => {
    e.preventDefault();
    e.currentTarget.setPointerCapture(e.pointerId);
    activePointersRef.current.set(e.pointerId, {
      clientX: e.clientX,
      clientY: e.clientY,
    });

    const activePointers = Array.from(activePointersRef.current.values());
    if (activePointers.length >= 2) {
      const pinch = getPinchMetrics(activePointers);
      if (pinch) {
        pinchRef.current = {
          startDistance: pinch.distance,
          startZoom: zoomRef.current,
          startPanX: panRef.current.x,
          startPanY: panRef.current.y,
          centerX: pinch.centerX,
          centerY: pinch.centerY,
        };
      }
      dragRef.current = null;
      setIsDragging(false);
      return;
    }

    setIsDragging(true);
    dragRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      startPanX: panRef.current.x,
      startPanY: panRef.current.y,
    };
  }, [getPinchMetrics, panRef]);

  const onPointerMove = useCallback((e) => {
    if (activePointersRef.current.has(e.pointerId)) {
      e.preventDefault();
      activePointersRef.current.set(e.pointerId, {
        clientX: e.clientX,
        clientY: e.clientY,
      });
    }

    const activePointers = Array.from(activePointersRef.current.values());
    if (activePointers.length >= 2) {
      const pinch = getPinchMetrics(activePointers);
      const start = pinchRef.current;
      if (!pinch || !start) return;
      const nextZoom = clampZoom(start.startZoom * (pinch.distance / start.startDistance));
      const scale = nextZoom / start.startZoom;
      const newPan = {
        x: pinch.centerX - scale * (start.centerX - start.startPanX),
        y: pinch.centerY - scale * (start.centerY - start.startPanY),
      };
      panRef.current = newPan;
      setPan(newPan);
      setZoom(nextZoom);
      return;
    }

    if (!dragRef.current) return;
    const dx = e.clientX - dragRef.current.startX;
    const dy = e.clientY - dragRef.current.startY;
    const newPan = { x: dragRef.current.startPanX + dx, y: dragRef.current.startPanY + dy };
    panRef.current = newPan;
    setPan(newPan);
  }, [clampZoom, getPinchMetrics, setPan, setZoom, panRef]);

  const onPointerUp = useCallback((e) => {
    activePointersRef.current.delete(e.pointerId);
    pinchRef.current = null;
    dragRef.current = null;
    setIsDragging(false);
  }, []);

  const draw = useCallback(async () => {
    const renderId = ++renderIdRef.current;
    const canvas = canvasRef.current;
    if (!canvas) return;

    const curZoom = zoom;
    const effectiveScale = Math.min(16, Math.max(RENDER_SCALE, Math.ceil(curZoom * 2)));
    const newW = canvasWidth * effectiveScale;
    const newH = canvasHeight * effectiveScale;
    if (canvas.width !== newW || canvas.height !== newH) {
      canvas.width = newW;
      canvas.height = newH;
    }

    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.save();
    ctx.scale(effectiveScale, effectiveScale);

    const W = canvasWidth, H = canvasHeight;
    const CX = W / 2, CY = H / 2;
    const PAD = 12;

    // 1. Fabric background
    const fg = ctx.createLinearGradient(0, 0, W, H);
    fg.addColorStop(0, "#f5efe5");
    fg.addColorStop(0.3, "#f0e9db");
    fg.addColorStop(0.7, "#ebe2d2");
    fg.addColorStop(1, "#e4d9c6");
    ctx.fillStyle = fg;
    ctx.fillRect(0, 0, W, H);

    // 2. Woven fabric texture
    ctx.save();
    ctx.lineCap = "butt";
    const weave = 1.5;
    ctx.strokeStyle = "#9a8260";
    ctx.lineWidth = 0.28;
    ctx.globalAlpha = 0.055;
    ctx.beginPath();
    for (let yy = 0; yy <= H; yy += weave) { ctx.moveTo(0, yy); ctx.lineTo(W, yy); }
    ctx.stroke();
    ctx.lineWidth = 0.5;
    ctx.globalAlpha = 0.032;
    ctx.beginPath();
    for (let yy = 0; yy <= H; yy += weave * 3) { ctx.moveTo(0, yy); ctx.lineTo(W, yy); }
    ctx.stroke();
    ctx.lineWidth = 0.28;
    ctx.globalAlpha = 0.045;
    ctx.beginPath();
    for (let xx = 0; xx <= W; xx += weave) { ctx.moveTo(xx, 0); ctx.lineTo(xx, H); }
    ctx.stroke();
    ctx.lineWidth = 0.5;
    ctx.globalAlpha = 0.028;
    ctx.beginPath();
    for (let xx = 0; xx <= W; xx += weave * 3) { ctx.moveTo(xx, 0); ctx.lineTo(xx, H); }
    ctx.stroke();
    ctx.restore();

    // 2b. Vignette
    ctx.save();
    const vigR = Math.max(W, H) * 0.7;
    const vig = ctx.createRadialGradient(CX, CY, vigR * 0.5, CX, CY, vigR);
    vig.addColorStop(0, "rgba(0,0,0,0)");
    vig.addColorStop(1, "rgba(40,25,10,0.06)");
    ctx.fillStyle = vig;
    ctx.fillRect(0, 0, W, H);
    ctx.restore();

    // 3. Design rendering
    if (realStitchData && realStitchData.stitches.length > 0) {
      ctx.save();
      const { stitches, colors: threadColors, minX, maxX, minY, maxY } = realStitchData;
      const subsampleLimit = Math.min(200000, Math.round(50000 * Math.max(1, curZoom)));
      const display = technical ? stitches : subsampleStitches(stitches, subsampleLimit);

      const designW = (maxX - minX) || 1;
      const designH = (maxY - minY) || 1;
      const fitW = W - PAD * 2;
      const fitH = H - PAD * 2;
      const scl = Math.min(fitW / designW, fitH / designH);
      const ofsX = CX - ((minX + maxX) / 2) * scl;
      const ofsY = CY - ((minY + maxY) / 2) * scl;
      const mapX = (v) => v * scl + ofsX;
      const mapY = (v) => v * scl + ofsY;

      if (technical) {
        drawTechnicalStitches(ctx, realStitchData, technical, { W, H, zoom: curZoom, mapX, mapY, scale: scl });
      } else {
      const segments = [];
      for (let i = 1; i < display.length; i++) {
        const p = display[i - 1], c = display[i];
        if (c.jump || c.colorIndex !== p.colorIndex) continue;
        segments.push({ x1: mapX(p.x), y1: mapY(p.y), x2: mapX(c.x), y2: mapY(c.y), ci: c.colorIndex });
      }

      const byColor = {};
      for (const seg of segments) (byColor[seg.ci] || (byColor[seg.ci] = [])).push(seg);

      const THREAD_MM = 0.4;
      const threadPx = THREAD_MM * 10 * scl;
      const TW = Math.max(0.6, Math.min(2.8, threadPx));

      const lightAz = (154 * Math.PI) / 180;
      const lightEl = (112 * Math.PI) / 180;
      const lx = Math.cos(lightEl) * Math.cos(lightAz);
      const ly = Math.cos(lightEl) * Math.sin(lightAz);
      const lz = Math.sin(lightEl);
      const hx = lx, hy = ly, hz = lz + 1;
      const hLen = Math.sqrt(hx * hx + hy * hy + hz * hz);
      const hnx = hx / hLen, hny = hy / hLen, hnz = hz / hLen;

      ctx.lineCap = "round";
      ctx.lineJoin = "round";

      ctx.globalCompositeOperation = "source-over";
      ctx.globalAlpha = 0.09;
      ctx.lineWidth = TW * 1.5;
      ctx.strokeStyle = "rgba(15,8,0,1)";
      ctx.beginPath();
      for (const seg of segments) {
        ctx.moveTo(seg.x1 + 0.35, seg.y1 + 0.5);
        ctx.lineTo(seg.x2 + 0.35, seg.y2 + 0.5);
      }
      ctx.stroke();

      ctx.globalAlpha = 1.0;
      ctx.lineWidth = TW;
      for (const [ci, segs] of Object.entries(byColor)) {
        const base = threadColors[ci] ?? "#333";
        ctx.strokeStyle = darkerHex(base);
        ctx.beginPath();
        for (const s of segs) { ctx.moveTo(s.x1, s.y1); ctx.lineTo(s.x2, s.y2); }
        ctx.stroke();
      }

      ctx.lineWidth = TW * 0.72;
      for (const [ci, segs] of Object.entries(byColor)) {
        const base = threadColors[ci] ?? "#333";
        ctx.strokeStyle = threadBaseColor(base);
        ctx.beginPath();
        for (const s of segs) { ctx.moveTo(s.x1, s.y1); ctx.lineTo(s.x2, s.y2); }
        ctx.stroke();
      }

      const NB = 6;
      const buckets = Array.from({ length: NB }, () => ({}));
      for (const seg of segments) {
        const dx = seg.x2 - seg.x1, dy = seg.y2 - seg.y1;
        const len = Math.sqrt(dx * dx + dy * dy);
        if (len < 0.3) continue;
        const nx = -dy / len, ny = dx / len, nz = 0.6;
        const nLen = Math.sqrt(nx * nx + ny * ny + nz * nz);
        const dot = Math.max(0, (nx / nLen) * hnx + (ny / nLen) * hny + (nz / nLen) * hnz);
        const spec = Math.pow(dot, 2.0) * 0.7;
        if (spec < 0.04) continue;
        const b = Math.min(NB - 1, Math.floor((spec * NB) / 0.7));
        (buckets[b][seg.ci] || (buckets[b][seg.ci] = [])).push(seg);
      }

      ctx.lineWidth = TW * 0.48;
      for (let b = 0; b < NB; b++) {
        const t = (b + 1) / NB;
        ctx.globalAlpha = t * 0.38;
        for (const [ci, segs] of Object.entries(buckets[b])) {
          const base = threadColors[ci] ?? "#333";
          ctx.strokeStyle = lightenHex(base, Math.round(t * 160));
          ctx.beginPath();
          for (const s of segs) { ctx.moveTo(s.x1, s.y1); ctx.lineTo(s.x2, s.y2); }
          ctx.stroke();
        }
      }

      ctx.lineWidth = Math.max(0.2, TW * 0.12);
      ctx.globalAlpha = 0.15;
      for (const [ci, segs] of Object.entries(byColor)) {
        const base = threadColors[ci] ?? "#333";
        ctx.strokeStyle = lightenHex(base, 210);
        ctx.beginPath();
        for (const s of segs) { ctx.moveTo(s.x1, s.y1); ctx.lineTo(s.x2, s.y2); }
        ctx.stroke();
      }

      }
      ctx.restore();
    } else if (svgJoined) {
      try {
        const svgStr = stringify(svgJoined);
        const blob = new Blob([svgStr], { type: "image/svg+xml" });
        const url = URL.createObjectURL(blob);
        await new Promise((resolve) => {
          const img = new Image();
          img.onload = () => {
            if (renderIdRef.current !== renderId) { URL.revokeObjectURL(url); resolve(); return; }
            const aspect = (img.naturalWidth || 100) / (img.naturalHeight || 100);
            const maxDim = Math.min(W - PAD * 2, H - PAD * 2);
            let dw = maxDim, dh = maxDim / aspect;
            if (dh > maxDim) { dh = maxDim; dw = maxDim * aspect; }

            const iw = Math.round(dw), ih = Math.round(dh);
            const off = document.createElement("canvas");
            off.width = iw; off.height = ih;
            const offCtx = off.getContext("2d");
            offCtx.drawImage(img, 0, 0, iw, ih);
            URL.revokeObjectURL(url);

            const imgData = offCtx.getImageData(0, 0, iw, ih);
            const px = imgData.data;
            const getC = (x, y) => { const i = (y * iw + x) * 4; return [px[i], px[i + 1], px[i + 2], px[i + 3]]; };
            const corners = [getC(0, 0), getC(iw - 1, 0), getC(0, ih - 1), getC(iw - 1, ih - 1)];
            // Only colour-key out a *solid* background. The joined SVG is normally
            // transparent, so its corners read as transparent black (0,0,0) — keying
            // against that would erase any black/dark layer (a missing colour). Skip
            // removal entirely unless the corners are actually opaque.
            const bgA = corners.reduce((s, c) => s + c[3], 0) / 4;
            if (bgA > 32) {
              const bgR = corners.reduce((s, c) => s + c[0], 0) / 4;
              const bgG = corners.reduce((s, c) => s + c[1], 0) / 4;
              const bgB = corners.reduce((s, c) => s + c[2], 0) / 4;
              const thr = 45;
              for (let i = 0; i < px.length; i += 4) {
                if (px[i + 3] === 0) continue;
                const d = Math.sqrt((px[i] - bgR) ** 2 + (px[i + 1] - bgG) ** 2 + (px[i + 2] - bgB) ** 2);
                if (d < thr) px[i + 3] = 0;
              }
              offCtx.putImageData(imgData, 0, 0);
            }

            ctx.save();
            ctx.drawImage(off, CX - dw / 2, CY - dh / 2, dw, dh);
            ctx.globalCompositeOperation = "source-over";
            ctx.lineCap = "round";

            const R = Math.min(W, H) / 2 - PAD;
            const angleDeg = stitchSettings?.angle ?? 45;
            const rad = (angleDeg * Math.PI) / 180;
            const spacing = Math.max(2, (stitchSettings?.row_spacing_mm ?? 0.35) * 28);
            const lineLen = W * 2;
            const stitchType = stitchSettings?.stitch_type || "auto_fill";

            if (stitchType === "auto_fill") {
              ctx.strokeStyle = "rgba(30,15,5,1)";
              ctx.lineWidth = Math.max(0.5, (stitchSettings?.row_spacing_mm ?? 0.35) * 2.2);
              ctx.globalAlpha = 0.07;
              ctx.setLineDash([2.5, 1.2]);
              for (let i = -lineLen; i < lineLen; i += spacing) {
                ctx.beginPath();
                ctx.moveTo(CX + i * Math.cos(rad) - lineLen * Math.sin(rad), CY + i * Math.sin(rad) + lineLen * Math.cos(rad));
                ctx.lineTo(CX + i * Math.cos(rad) + lineLen * Math.sin(rad), CY + i * Math.sin(rad) - lineLen * Math.cos(rad));
                ctx.stroke();
              }
              ctx.setLineDash([]);
            } else if (stitchType === "cross_stitch") {
              ctx.strokeStyle = "rgba(30,15,5,1)";
              ctx.lineWidth = 0.6;
              ctx.globalAlpha = 0.06;
              const xSpacing = spacing * 1.4;
              [Math.PI / 4, -Math.PI / 4].forEach((dr) => {
                for (let i = -lineLen; i < lineLen; i += xSpacing) {
                  ctx.beginPath();
                  ctx.moveTo(CX + i * Math.cos(dr) - lineLen * Math.sin(dr), CY + i * Math.sin(dr) + lineLen * Math.cos(dr));
                  ctx.lineTo(CX + i * Math.cos(dr) + lineLen * Math.sin(dr), CY + i * Math.sin(dr) - lineLen * Math.cos(dr));
                  ctx.stroke();
                }
              });
            } else if (stitchType === "meander") {
              ctx.strokeStyle = "rgba(30,15,5,1)";
              ctx.lineWidth = 1;
              ctx.globalAlpha = 0.08;
              ctx.beginPath();
              const turns = Math.floor((R * 0.8) / (spacing * 0.9));
              const totalAngle = turns * Math.PI * 2;
              for (let t2 = 0; t2 <= totalAngle; t2 += 0.08) {
                const spiralR = (t2 / totalAngle) * R * 0.82;
                const px2 = CX + spiralR * Math.cos(t2);
                const py2 = CY + spiralR * Math.sin(t2);
                t2 === 0 ? ctx.moveTo(px2, py2) : ctx.lineTo(px2, py2);
              }
              ctx.stroke();
            }

            if (stitchSettings?.fill_underlay && (stitchType === "auto_fill" || stitchType === "cross_stitch")) {
              const underlayRad = ((stitchSettings?.angle ?? 45) * Math.PI) / 180 + Math.PI / 2;
              const underlaySpacing = Math.max(5, (stitchSettings?.fill_underlay_row_spacing_mm ?? 3.0) * 9);
              ctx.strokeStyle = "rgba(30,15,5,0.5)";
              ctx.lineWidth = 0.4;
              ctx.globalAlpha = 0.04;
              for (let i = -lineLen; i < lineLen; i += underlaySpacing) {
                ctx.beginPath();
                ctx.moveTo(CX + i * Math.cos(underlayRad) - lineLen * Math.sin(underlayRad), CY + i * Math.sin(underlayRad) + lineLen * Math.cos(underlayRad));
                ctx.lineTo(CX + i * Math.cos(underlayRad) + lineLen * Math.sin(underlayRad), CY + i * Math.sin(underlayRad) - lineLen * Math.cos(underlayRad));
                ctx.stroke();
              }
            }

            ctx.restore();
            resolve();
          };
          img.onerror = () => { URL.revokeObjectURL(url); resolve(); };
          img.src = url;
        });
      } catch (e) { /* preview is non-critical */ }
    } else {
      ctx.save();
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillStyle = "rgba(100,80,60,0.18)";
      ctx.font = "500 12px Inter, system-ui, sans-serif";
      ctx.fillText("Upload a design", CX, CY - 6);
      ctx.fillStyle = "rgba(100,80,60,0.12)";
      ctx.font = "400 9px Inter, system-ui, sans-serif";
      ctx.fillText("to preview stitches", CX, CY + 8);
      ctx.restore();
    }

    if (renderIdRef.current !== renderId) return;

    // 4. Size label
    ctx.save();
    const sizeText = realStitchData
      ? `Stitch bounds: ${((realStitchData.maxX - realStitchData.minX) / 10).toFixed(1)} × ${((realStitchData.maxY - realStitchData.minY) / 10).toFixed(1)} mm`
      : `${targetWidth} × ${targetHeight} mm`;
    ctx.font = "500 8px Inter, system-ui, sans-serif";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    const labelY = H - 8;
    const labelW = ctx.measureText(sizeText).width + 10;
    ctx.fillStyle = "rgba(60,45,25,0.12)";
    if (ctx.roundRect) {
      ctx.beginPath();
      ctx.roundRect(CX - labelW / 2, labelY - 6, labelW, 12, 3);
      ctx.fill();
    } else {
      ctx.fillRect(CX - labelW / 2, labelY - 6, labelW, 12);
    }
    ctx.fillStyle = "rgba(80,60,40,0.55)";
    ctx.fillText(sizeText, CX, labelY);
    ctx.restore();

    ctx.restore();
  }, [svgJoined, stitchSettings, targetWidth, targetHeight, realStitchData, zoom, canvasWidth, canvasHeight, technical]);

  useEffect(() => {
    const t = setTimeout(() => draw(), 120);
    return () => clearTimeout(t);
  }, [draw]);

  return (
    <div
      ref={containerRef}
      style={{
        width: `${VW}px`,
        height: `${VH}px`,
        overflow: "hidden",
        margin: "0 auto",
        borderRadius: "10px",
        cursor: isDragging ? "grabbing" : "grab",
        userSelect: "none",
        touchAction: "none",
        background: "#e2d8c8",
        boxShadow: "inset 0 2px 10px rgba(0,0,0,0.12), inset 0 -1px 4px rgba(0,0,0,0.06)",
        border: "1px solid rgba(255,255,255,0.08)",
      }}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
    >
      <canvas
        ref={canvasRef}
        width={BASE_W * RENDER_SCALE}
        height={BASE_H * RENDER_SCALE}
        style={{
          display: "block",
          width: `${canvasWidth}px`,
          height: `${canvasHeight}px`,
          transformOrigin: "0 0",
          transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
          willChange: "transform",
          imageRendering: "auto",
        }}
      />
    </div>
  );
};

export default StitchPreviewCanvas;
